import { humanize } from '@utils';
import { BACKEND_NAME_SEPARATOR, MAX_ALLOWED_LEVEL, NAME_SEPARATOR } from './constants';
import { isAppModule } from '@src/utils/constants';

export const isFirstDimension = (dimension) => dimension === 1;

export const getOptionsList = (options) => options.map((item) => ({ value: item }));

export const getDimensionState = (dimension) => [...Array(dimension)];

export const updateDimensionState = (state, ind, select) => {
  const newState = [...state];
  newState[ind] = select;

  return newState;
};

export const getFilteredOptions = (input, option) =>
  (option?.value.toLowerCase() ?? '').includes(input.toLowerCase());

export const getDimensionNameList = (name: string): string[] =>
  name.split(NAME_SEPARATOR).map((name) => name.trim());

export const getDimensionNameArr = (selectList) =>
  selectList.map((dimension) => dimension?.value ?? '');

export const getDimensionNameStr = (selectList) =>
  getDimensionNameArr(selectList).join(NAME_SEPARATOR);

export const getBackendNameList = (name: string): string[] =>
  name.split(BACKEND_NAME_SEPARATOR).map((name) => name.trim());

export const getBackendNameArr = (selectList) =>
  selectList.map((dimension) => dimension?.backendName ?? '');

export const getBackendNameStr = (selectList) =>
  getBackendNameArr(selectList).join(BACKEND_NAME_SEPARATOR);

export const getInsertIndex = (aiAlertsConfig, level) =>
  aiAlertsConfig[level].dimensionData.findIndex(({ isCustomDimension }) => !isCustomDimension);

export const getDimensionList = (aiConfigList, aiDimensionMapping) =>
  aiConfigList
    .filter(({ level }) => level === 1)
    .reduce((dimAcc, curr) => {
      const { dimensionData } = curr;
      const dimensionException = ['ASSETS', 'OBJECT']; // Exception to hide dimensions at level 2 & level 3 combinations dropdown list
      dimAcc = dimensionData
        .filter(({ isBeingDeleted, backendName }) => {
          if (isBeingDeleted) return false;
          if (
            dimensionException.includes(backendName) &&
            !aiDimensionMapping[backendName].isCustomDimension
          )
            return false;
          return true;
        })
        .map((dimension) => ({ value: dimension.name, ...dimension }));

      return dimAcc;
    }, []);

export const getNewDimensionCombination = (selectList, level) => {
  // Level-1 single-dim adds of a standard mapping entry must carry isCustomDimension: false
  // so saveAiAlertsConfigCombination routes them into survivingStandardDimsArr (CCS knowledge_graph),
  // not customDimensionArr. Level-2+ combos stay marked custom because non-default-KG combos would
  // otherwise be dropped by applySynopsisDeltasToVsi.
  const isLevel1Standard =
    level === 1 && selectList.length === 1 && selectList[0]?.isCustomDimension === false;
  return {
    name: getDimensionNameStr(selectList),
    backendName: getBackendNameStr(selectList),
    isCustomDimension: !isLevel1Standard,
    level,
    cardinality: 0,
  };
};

export const transformDimensionList = (
  aiAlertMapping = {},
  aiConfigList,
  level,
  allowStandardDims = false,
) => {
  return Object.keys(aiAlertMapping)
    .filter((backendName) => {
      const allTrafficException = ['CUSTOMER']; // Exception to hide all traffic while showing all other standard dimensions

      // DPI/ECO (isAppModule) uses simpler filtering
      //CE-11927: network_request_url_path is excluded from the dropdown list
      const dpiDimensionExceptions = ['network_request_url_path'];
      if (isAppModule) {
        return (
          !allTrafficException.includes(backendName) &&
          !dpiDimensionExceptions.includes(backendName)
        );
      }

      // VSI/EI uses complex filtering with multiple exceptions
      const dimensionExceptions = [
        'CHANNEL',
        'STREAMING_PROTOCOL',
        'AD_CREATIVE_ID',
        'AD_ID',
        'AD_TECHNOLOGY',
        'CDN_EDGESERVER',
        'CDN_EDGEGROUP',
      ];
      // CE-11927: Dimensions based on TagKey that are not supported by AI Alerts backend
      const tagKeyExceptions = ['RESOURCE_ID', 'AD_REQUESTED'];
      const geoGroupByExceptions = ['GEO_STATE']; // Exception to hide group_by items in the dropdown list, GEO_STATE is always hidden
      const { field, key } = aiAlertMapping[backendName];

      return !(
        allTrafficException.includes(backendName) ||
        dimensionExceptions.includes(backendName) ||
        tagKeyExceptions.includes(key) ||
        geoGroupByExceptions.includes(field)
      );
    })
    .filter((backendName) => {
      // When allowStandardDims is false (default / FF off), keep the legacy behavior of
      // surfacing only custom dims in the dropdown. DPI/ECO keeps its existing semantics.
      if (allowStandardDims || isAppModule) return true;
      return aiAlertMapping[backendName]?.isCustomDimension === true;
    })
    .filter((backendName) => {
      if (level !== 1) {
        return true;
      }
      const curDimension = aiAlertMapping[backendName];
      const { pulse5Label } = curDimension;
      // filter out items in the dropdown select, if the dimension is already present in level 1.
      return !aiConfigList?.[1]?.dimensionData.some(
        (item) => item.name === pulse5Label || item.backendName === backendName,
      );
    })
    .filter(
      // The server keys customer custom dimensions under both `backendName` and the resolved
      // tagKey/groupBy alias so server-side alert resolution can look them up either way.
      // The dropdown only needs one entry per dimension; dedupe by pulse5Label and let the
      // first occurrence (which is the canonical backendName) win.
      ((seen) => (backendName) => {
        const label = aiAlertMapping[backendName]?.pulse5Label;
        if (!label) return false;
        if (seen.has(label)) return false;
        seen.add(label);
        return true;
      })(new Set<string>()),
    )
    .map((backendName) => {
      const currDimension = aiAlertMapping[backendName];
      const { pulse5Label } = currDimension;
      const dimensionMeta = {
        ...currDimension,
        value: pulse5Label,
        backendName,
      };
      return dimensionMeta;
    });
};

export const getExtensionText = (highlightDimensions) => {
  let text = '';
  const levels = highlightDimensions.map(({ level }) => level);
  const setLevels = [...new Set(levels)];
  setLevels.forEach((level) => {
    if (level === 1) text = 'dimension';
    else if (setLevels.length < 2) text = 'dimension combinations';
    else text = 'dimension combinations below';
  });

  return text;
};

// Overall count of scanned combinations (standard + custom) across levels 1..(MAX_ALLOWED_LEVEL-1),
// excluding All Traffic (level 0) and level >= MAX_ALLOWED_LEVEL. Used to enforce the dimension limit.
export const configuredDimensionCount = (allLevelDimensionData) => {
  let totalConfiguredDimensionCount = 0;

  allLevelDimensionData.forEach((curLevel) => {
    const { dimensionData, level } = curLevel;
    if (level === 0 || level >= MAX_ALLOWED_LEVEL) {
      return;
    }
    dimensionData.forEach(({ isBeingDeleted }) => {
      if (!isBeingDeleted) {
        totalConfiguredDimensionCount += 1;
      }
    });
  });

  return totalConfiguredDimensionCount;
};

const customRootCauseCount = (allLevelDimensionData, maxCount) => {
  return configuredDimensionCount(allLevelDimensionData) < maxCount;
};

export const isAdvanceCustomerType = (customerType) => {
  return customerType === 2;
};

export const shouldDisplayAddDim = (
  isFeatureFlagEnabled,
  allLevelDimensionData = [],
  maxCustomCount,
) => {
  if (!isFeatureFlagEnabled) {
    return false;
  }
  return customRootCauseCount(allLevelDimensionData, maxCustomCount);
};

export const getDeletionCombination = (state, combBackendName): boolean => {
  return state.some(({ backendName }) => backendName === combBackendName);
};

export const getCardinalityText = (
  cardinality,
  level,
  isFetching = false,
  isError = false,
  isBeingAdded = false,
  isBeingDeleted = false,
) => {
  switch (true) {
    case isBeingAdded:
      return '0 Values';
    case isBeingDeleted:
      return 'Deletion processing';
    case isFetching:
      return 'Loading..';
    case isError:
      return 'Error';
    default: {
      const prefixText = level === 0 ? 'Scanning ' : ''; // prefix for 0th level
      const suffixText = level === 1 ? 'Value' : 'Combination'; // Checking whether it's single dimension
      const isPlural = cardinality > 1 ? 's' : '';

      return `${prefixText}${humanize(cardinality, '')} ${suffixText}${isPlural}`;
    }
  }
};

export const getDeleteText = (isBeingAdded: boolean, isBeingDeleted: boolean): string => {
  switch (true) {
    case isBeingAdded:
      return 'Dimension Values which meet the minimum traffic conditions are counted. If you just added the dimension, wait for a couple of hours.';
    case isBeingDeleted:
      return 'Deletion from the system takes upto 1 hour time';
    default:
      return '';
  }
};

export const getHighLightCombinations = (
  aiConfigList,
  combination,
  level,
  backendName,
  allowStandardDims = false,
) => {
  const subCombinations = [
    {
      backendName,
      level,
    },
  ];

  // this is used for a BFS search
  const stack = [
    {
      backendName,
      level,
    },
  ];
  let curLevel = level + 1;
  while (curLevel < MAX_ALLOWED_LEVEL && stack.length > 0) {
    // check the node in every single level
    let curStackLength = stack.length;
    while (curStackLength > 0) {
      const curRoot = stack.shift();
      const curLevelCombinations = aiConfigList[curLevel];
      curLevelCombinations.dimensionData?.forEach((combinationData) => {
        const { isCustomDimension, backendName, isBeingDeleted } = combinationData;
        const backendList = getBackendNameList(backendName);
        const curRootBackendList = getBackendNameList(curRoot?.backendName);
        const hasCombination =
          (isCustomDimension || allowStandardDims) &&
          !isBeingDeleted &&
          curRootBackendList.every((dimension) =>
            backendList.some((curDimension) => curDimension === dimension),
          );
        if (hasCombination) {
          // this is the previous level combinations
          const preLevelCombination = aiConfigList[curLevel - 1];
          // need to double check to make sure it has more than one parent node that is not in the current result set
          // if it is in this case, we should keep this node
          const preLevelNodes = preLevelCombination.dimensionData?.filter((preCombination) => {
            const backendNameList = getBackendNameList(preCombination.backendName);
            const curBackendNameList = getBackendNameList(backendName);
            // the candidate node is not in the subCombinations
            if (
              subCombinations.find(
                (curResItem) => curResItem.backendName === preCombination.backendName,
              )
            ) {
              return false;
            }
            // Treat a previous-level node that is already in deletion as "not available",
            // i.e. it cannot count as a surviving parent of the current combo. Otherwise a
            // combo whose only "other" parent is being deleted would be incorrectly skipped
            // from the highlight set.
            if (preCombination.isBeingDeleted) {
              return false;
            }
            // this logic is used for checking if this node is connected to the previous level node
            return backendNameList.every((preDimension) => {
              return curBackendNameList.some((rDimension) => rDimension === preDimension);
            });
          });
          // if there is not any node meet this criteria, we need to delete it.
          if (preLevelNodes.length === 0) {
            stack.push({
              backendName,
              level: curLevel,
            });
            subCombinations.push({
              backendName,
              level: curLevel,
            });
          }
        }
      });
      curStackLength--;
    }
    curLevel++;
  }

  return subCombinations;
};

export const getHighlightColor = (redHighLight: boolean, isBeingDeleted: boolean): string => {
  switch (true) {
    case redHighLight:
      return '#FF7F7F';
    case isBeingDeleted:
      return '#979797';
    default:
      return '';
  }
};
